'use client'

import type { ReactNode } from 'react'

import type { NavGroup } from '@graftcode/design-system'
import { Icon, IconComponent } from '@graftcode/design-system'

import { AppVariant } from '../../../../enums'
import { useMobileMenuContext } from '../../context'

import styles from '../../MobileMenu.module.scss'

const APP_LABELS = {
  portal: { label: 'Portal', icon: Icon.GCPortal },
  gateway: { label: 'Gateway', icon: Icon.GCGateway },
  modules: { label: 'Modules', icon: Icon.GCModules },
  vision: { label: 'Vision', icon: Icon.GCVision },
  academy: { label: 'Academy', icon: Icon.GCAcademy },
  conference: { label: 'WAD', icon: Icon.GCConference },
} as const

interface MobileMenuNavigationProps {
  appVariant: AppVariant
  menuGroups: NavGroup[]
  onNavigationClick: (url: string) => void
  onAppHeaderClick?: () => void
  topSlot?: ReactNode
}

export function MobileMenuNavigation({
  appVariant,
  menuGroups,
  onNavigationClick,
  onAppHeaderClick,
  topSlot,
}: MobileMenuNavigationProps) {
  const appConfig = APP_LABELS[appVariant] || APP_LABELS.portal
  const { onClose } = useMobileMenuContext()

  const handleItemClick = (url: string) => {
    onNavigationClick(url)
    onClose()
  }

  return (
    <div className={styles.navigationSection}>
      {/* App Header - clickable to open app picker */}
      <button type="button" className={styles.appHeader} onClick={onAppHeaderClick}>
        <div className={styles.appHeaderContent}>
          <IconComponent icon={appConfig.icon} size={24} className={styles.appHeaderIcon} />
          <span className={styles.appHeaderLabel}>{appConfig.label}</span>
        </div>
        <IconComponent icon={Icon.ChevronRightRegular} size={20} />
      </button>

      {/* All links container - shared padding */}
      <div className={styles.allLinksContainer}>
        {/* Top Slot (Add new button etc.) */}
        {topSlot}

        {/* Navigation Items */}
        <div className={styles.navItems}>
          {menuGroups.map((group) => (
            <div
              key={`${group.label ?? 'group'}-${group.items.map((item) => `${item.url}:${item.label}`).join('|')}`}
              className={styles.navGroup}
            >
              {group.label && <div className={styles.navGroupLabel}>{group.label}</div>}
              {group.items.map((item) => (
                <button
                  key={`${item.url}-${item.label}-mobile`}
                  className={`${styles.navItem} ${item.active ? styles.active : ''} ${item.disabled ? styles.disabled : ''}`}
                  onClick={() => !item.disabled && handleItemClick(item.url)}
                  disabled={item.disabled}
                >
                  {item.icon && <IconComponent icon={item.icon} size={24} className={styles.navItemIcon} />}
                  <span className={styles.navItemLabel}>{item.label}</span>
                  {item.badge && <span className={styles.navItemBadge}>{item.badge}</span>}
                </button>
              ))}
            </div>
          ))}
        </div>
      </div>
    </div>
  )
}
